import supabase from '../supabase/supabase';

const TABLES = {
  learners: 'hms_academy_learners_1715725000000',
  organizations: 'organizations_1715680000000',
  courses: 'hms_academy_courses_1715725000000',
  versions: 'hms_academy_course_versions_1715725000000',
  enrollments: 'hms_academy_enrollments_1715726000000',
  units: 'hms_academy_learning_units_1715725000000',
  progress: 'hms_academy_unit_progress_1715727000000',
  assessments: 'hms_academy_assessments_1715728000000',
  attempts: 'hms_academy_assessment_attempts_1715728000000',
  completions: 'hms_academy_completions_1715727000000',
  certificates: 'hms_academy_certificates_1715729000000'
};

const PAGE_SIZE = 500;

const FIELDS = {
  learners: 'id,organization_id,first_name,last_name,email,status,updated_at',
  organizations: 'id,name',
  courses: 'id,title,status',
  versions: 'id,course_id,version,title,status',
  enrollments: 'id,learner_id,course_version_id,status,created_at,updated_at,activated_at',
  units: 'id,course_version_id,parent_unit_id,is_required',
  progress: 'id,enrollment_id,learning_unit_id,course_version_id,status,completed_at,last_accessed_at,updated_at',
  assessments: 'id,course_version_id,title,status',
  attempts: 'id,enrollment_id,assessment_id,status,score_percent,passed,graded_at',
  completions: 'id,enrollment_id,completed_at',
  certificates: 'id,completion_id,certificate_number,issued_at,status'
};

function fail(label, error) {
  throw new Error(`Kunne ikke lese ${label}: ${error.message}`);
}

async function readAll(table, fields, label) {
  const rows = [];
  let offset = 0;

  while (true) {
    const { data, error } = await supabase
      .from(table)
      .select(fields)
      .order('id', { ascending: true })
      .range(offset, offset + PAGE_SIZE - 1);

    if (error) fail(label, error);

    const page = data || [];
    rows.push(...page);
    if (page.length < PAGE_SIZE) return rows;
    offset += PAGE_SIZE;
  }
}

function latestDate(values) {
  const timestamps = values
    .filter(Boolean)
    .map((value) => new Date(value).getTime())
    .filter(Number.isFinite);

  return timestamps.length ? new Date(Math.max(...timestamps)).toISOString() : null;
}

function buildCourseLookups(courses, versions) {
  const coursesById = new Map(courses.map((row) => [row.id, row]));
  const versionsById = new Map(
    versions.map((row) => [row.id, {
      ...row,
      course: coursesById.get(row.course_id)
    }])
  );
  return { coursesById, versionsById };
}

function buildParticipant(learner, data, lookups) {
  const { versionsById, organizationsById, completionsByEnrollment,
    progressByEnrollment, unitsByVersion, attemptsByEnrollment,
    assessmentsById, certificatesByCompletion } = lookups;
  const enrollments = data.enrollments.filter(
    (row) => row.learner_id === learner.id
  );

  const courses = enrollments.map((enrollment) => {
    const version = versionsById.get(enrollment.course_version_id);
    const completion = completionsByEnrollment.get(enrollment.id);
    const requiredUnits = unitsByVersion.get(enrollment.course_version_id) || [];
    const completedUnits = new Set(
      (progressByEnrollment.get(enrollment.id) || [])
        .filter((row) => row.status === 'completed')
        .map((row) => row.learning_unit_id)
    );
    const completedRequired = requiredUnits.filter((unit) =>
      completedUnits.has(unit.id)
    ).length;
    const attempts = (attemptsByEnrollment.get(enrollment.id) || [])
      .map((attempt) => ({
        ...attempt,
        assessment: assessmentsById.get(attempt.assessment_id)
      }))
      .sort((a, b) => new Date(b.graded_at || 0) - new Date(a.graded_at || 0));
    const certificates = completion
      ? certificatesByCompletion.get(completion.id) || []
      : [];

    return {
      id: enrollment.id,
      enrollmentStatus: enrollment.status,
      enrolledAt: enrollment.created_at,
      courseName: version?.course?.title || version?.title || 'Ukjent kurs',
      version: version?.version || '',
      completionDate: completion?.completed_at || null,
      progress: requiredUnits.length
        ? Math.round((completedRequired / requiredUnits.length) * 100)
        : completion ? 100 : 0,
      requiredUnits: requiredUnits.length,
      completedUnits: completedRequired,
      attempts,
      certificates
    };
  });

  const activityDates = [
    learner.updated_at,
    ...enrollments.flatMap((row) => [row.created_at, row.updated_at]),
    ...courses.flatMap((row) => [
      row.completionDate,
      ...row.attempts.map((attempt) => attempt.graded_at),
      ...row.certificates.map((certificate) => certificate.issued_at)
    ]),
    ...data.progress
      .filter((row) => enrollments.some((enrollment) => enrollment.id === row.enrollment_id))
      .flatMap((row) => [row.last_accessed_at, row.updated_at])
  ];

  return {
    id: learner.id,
    name: `${learner.first_name} ${learner.last_name}`.trim(),
    email: learner.email || '',
    status: learner.status,
    organizationId: learner.organization_id,
    organizationName: organizationsById.get(learner.organization_id)?.name || 'Ikke oppgitt',
    enrollmentCount: enrollments.length,
    completionCount: courses.filter((course) => course.completionDate).length,
    certificateCount: courses.reduce(
      (count, course) => count + course.certificates.length,
      0
    ),
    lastActivity: latestDate(activityDates),
    courses
  };
}

export async function getAcademyParticipants() {
  const labels = [
    'Academy-deltakere',
    'organisasjoner',
    'Academy-kurs',
    'kursversjoner',
    'påmeldinger',
    'kursenheter',
    'fremdrift',
    'kunnskapstester',
    'testresultater',
    'fullføringer',
    'kursbevis'
  ];
  const tables = Object.keys(TABLES);

  const results = await Promise.all(
    tables.map((key, index) => readAll(TABLES[key], FIELDS[key], labels[index]))
  );
  const data = Object.fromEntries(tables.map((key, index) => [key, results[index]]));
  const { versionsById } = buildCourseLookups(data.courses, data.versions);

  const organizationsById = new Map(
    data.organizations.map((row) => [row.id, row.name])
  );
  const completionsByEnrollment = new Map(
    data.completions.map((row) => [row.enrollment_id, row])
  );
  const progressByEnrollment = new Map();
  data.progress.forEach((row) => {
    const list = progressByEnrollment.get(row.enrollment_id) || [];
    list.push(row);
    progressByEnrollment.set(row.enrollment_id, list);
  });

  const unitsByVersion = new Map();
  data.units
    .filter((unit) => unit.is_required && !data.units.some(
      (child) => child.parent_unit_id === unit.id &&
        child.course_version_id === unit.course_version_id
    ))
    .forEach((unit) => {
      const list = unitsByVersion.get(unit.course_version_id) || [];
      list.push(unit);
      unitsByVersion.set(unit.course_version_id, list);
    });

  const attemptsByEnrollment = new Map();
  data.attempts.forEach((attempt) => {
    const list = attemptsByEnrollment.get(attempt.enrollment_id) || [];
    list.push(attempt);
    attemptsByEnrollment.set(attempt.enrollment_id, list);
  });

  const certificatesByCompletion = new Map();
  data.certificates
    .filter((row) => row.status === 'issued')
    .forEach((certificate) => {
      const list = certificatesByCompletion.get(certificate.completion_id) || [];
      list.push(certificate);
      certificatesByCompletion.set(certificate.completion_id, list);
    });

  const participants = data.learners
    .map((learner) => buildParticipant(learner, data, {
      versionsById,
      organizationsById,
      completionsByEnrollment,
      progressByEnrollment,
      unitsByVersion,
      attemptsByEnrollment,
      assessmentsById: new Map(data.assessments.map((row) => [row.id, row])),
      certificatesByCompletion
    }))
    .sort((a, b) => a.name.localeCompare(b.name, 'nb'));

  return {
    participants,
    organizations: data.organizations
      .map((row) => ({ id: row.id, name: row.name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'nb'))
  };
}